import styled, { css } from "styled-components";

import Button from "../../components/Buttons/Button";
import { Input as SharedInput } from "../../components/IOSCompatibleInput/IOSCompatibleInput.style";
import { pxToRem, withAlpha } from "../../styles/utils";

export const LinkPageShell = styled.main`
  min-height: var(--real-vh, 100vh);
  padding: max(24px, env(safe-area-inset-top)) 20px max(28px, env(safe-area-inset-bottom));
  background: ${({ theme }) => theme.colors.canvas};
  color: ${({ theme }) => theme.colors.ink};
`;

export const LinkPageContent = styled.section`
  width: min(100%, 420px);
  margin: 0 auto;
`;

export const LinkPageTitle = styled.h1`
  margin: 0 0 34px;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(28)};
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;

  @media (max-width: 420px) {
    font-size: ${pxToRem(26)};
  }
`;

export const LinkSectionTitle = styled.h2`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
`;

export const LinkLead = styled.p`
  max-width: 37ch;
  margin: 0 0 28px;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.55;
`;

export const LinkForm = styled.form`
  width: 100%;
`;

export const LinkField = styled.div`
  margin-bottom: 16px;
`;

export const LinkLabel = styled.label`
  display: block;
  margin: 0 0 7px;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(13)};
  font-weight: 600;
  line-height: 1.35;
`;

export const LinkInputWrap = styled.div`
  position: relative;
`;

export const LinkInput = styled(SharedInput).withConfig({
  shouldForwardProp: (prop) => prop !== "$hasError",
})<{ $hasError?: boolean }>`
  height: 52px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.white};
  --autofill-background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.brand, 0.35)};
  }

  &:focus,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.white};
    --autofill-background: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 0 3px ${({ theme }) => withAlpha(theme.colors.brand, 0.1)};
  }

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.danger};
      background: ${theme.colors.white};
      --autofill-background: ${theme.colors.white};
    `}
`;

export const PasswordInput = styled(LinkInput)`
  padding-right: 52px;
`;

export const PasswordToggle = styled.button`
  position: absolute;
  top: 50%;
  right: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.grayIcon};
  cursor: pointer;
  transform: translateY(-50%);

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brand};
    outline-offset: 1px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const LinkFieldError = styled.div`
  min-height: 18px;
  margin-top: 5px;
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.35;
`;

export const LinkPrimaryAction = styled(Button)`
  width: 100%;
  max-width: none;
  height: 52px;
  margin-top: 6px;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const LinkStateAction = styled(LinkPrimaryAction)`
  margin-top: 20px;
`;

export const LinkFootnote = styled.p`
  margin: 18px 4px 0;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.5;
  text-align: center;
`;

export const SuccessState = styled.div`
  padding-top: 18px;
`;

export const SuccessIconCircle = styled.div`
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin-bottom: 22px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 8px 22px ${({ theme }) => withAlpha(theme.colors.success, 0.18)};

  svg {
    width: 24px;
    height: 24px;
    color: currentColor;
  }

  svg path,
  svg polyline,
  svg line {
    stroke: currentColor;
  }
`;

export const SuccessText = styled.p`
  max-width: 36ch;
  margin: 0 0 26px;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.55;
`;

export const LinkStateMessage = styled.p<{ $danger?: boolean }>`
  margin: 0;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.dangerText : theme.colors.inkSoft)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.55;
`;
